import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { OrganizationModel } from '../models/organizations/organization-model';
import { OrganizationMemberModel } from '../models/organizations/organization-member-model';
import {
  OrganizationInviteMemberModel,
  OrganizationMemberInvitationModel,
  OrganizationBusinessDetailsModel,
} from '../models/organizations';
import { OrganizationUpdateMemberModel } from '../models/organizations/organization-update-member-model';
import { enumRespErrors } from '../enums/common';
import { AlertService } from '@services/alert.service';
import { CompanyModel } from '../models/companies';

@Injectable({
  providedIn: 'root',
})
export class OrganizationsRepository {
  constructor(private apiService: ApiService, public alertService: AlertService) {}

  public getOrganizations(isShowLoader: boolean = true): Observable<OrganizationModel[]> {
    return this.apiService.get<OrganizationModel[]>(environment.url.organizations, null, { isShowLoader });
  }

  public getOrganizationById(organizationId: number, isShowLoader: boolean = true): Observable<OrganizationModel> {
    return this.apiService.get<OrganizationModel>(`${environment.url.organizations}${organizationId}`, null, { isShowLoader });
  }

  public getOrganizationCompanies(organizationId: number, isShowLoader: boolean = true): Observable<CompanyModel[]> {
    return this.apiService.get<CompanyModel[]>(`${environment.url.organizations}${organizationId}/companies`, null, { isShowLoader });
  }

  public getOrganizationMembers(organizationId: number, isShowLoader: boolean = true): Observable<OrganizationMemberModel[]> {
    return this.apiService.get<OrganizationMemberModel[]>(`${environment.url.organizations}${organizationId}/members`, null, {
      isShowLoader,
    });
  }

  public getOrganizationInvitedMembers(
    organizationId: number,
    isShowLoader: boolean = true,
  ): Observable<OrganizationMemberInvitationModel[]> {
    return this.apiService.get<OrganizationMemberInvitationModel[]>(
      `${environment.url.organizations}${organizationId}/invited-members`,
      null,
      {
        isShowLoader,
      },
    );
  }

  public getOrganizationCompanyMembers(
    organizationId: number,
    companyId: number,
    isShowLoader: boolean = true,
  ): Observable<OrganizationMemberModel[]> {
    return this.apiService.get<OrganizationMemberModel[]>(
      `${environment.url.organizations}${organizationId}/companies/${companyId}/members`,
      null,
      { isShowLoader },
    );
  }

  public getOrganizationCompanyInvitedMembers(
    organizationId: number,
    companyId: number,
    isShowLoader: boolean = true,
  ): Observable<OrganizationMemberInvitationModel[]> {
    return this.apiService.get<OrganizationMemberInvitationModel[]>(
      `${environment.url.organizations}${organizationId}/companies/${companyId}/invited-members`,
      null,
      { isShowLoader },
    );
  }

  public inviteOrganizationMember(
    organizationId: number,
    data: OrganizationInviteMemberModel,
    isShowLoader?: boolean,
  ): Observable<OrganizationInviteMemberModel> {
    return this.apiService.post<OrganizationInviteMemberModel>(`${environment.url.organizations}${organizationId}/members`, data, null, {
      isShowLoader,
    });
  }

  public resendOrganizationMemberInvite(invitationId: number, isShowLoader?: boolean): Observable<never> {
    return this.apiService.put(`${environment.url.organizationInvitations}${invitationId}`, null, null, {
      isShowLoader,
    });
  }

  public updateOrganizationMember(
    organizationId: number,
    userId: string,
    data: OrganizationUpdateMemberModel,
    isShowLoader?: boolean,
  ): Observable<OrganizationInviteMemberModel> {
    return this.apiService.put<OrganizationInviteMemberModel>(
      `${environment.url.organizations}${organizationId}/members/${userId}`,
      data,
      null,
      {
        isShowLoader,
      },
    );
  }

  public deleteOrganizationMember(organizationId: number, userId: number, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.delete(`${environment.url.organizations}${organizationId}/members/${userId}`, null, { isShowLoader });
  }

  public deleteOrganizationMemberInvitation(invitationId: number, isShowLoader?: boolean): Observable<never> {
    return this.apiService.delete(`${environment.url.organizationInvitations}${invitationId}`, null, { isShowLoader });
  }

  public acceptOrganizationInvitation(organizationInvitationId: number, token?: string): Observable<any> {
    const options: any = !!token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : null;
    return this.apiService.post(
      `${environment.url.organizations}invitations/${organizationInvitationId}`,
      null,
      options,
      null,
      this.handleInvitationError.bind(this),
    );
  }

  public handleInvitationError(err: HttpErrorResponse): Observable<any> {
    const errors: { [name: string]: string } = {
      invitation_overdue: 'BACK_ERROR.INVITATION_OVERDUE',
      invitation_already_accepted: 'BACK_ERROR.INVITATION_ALREADY_ACCEPTED',
      invite_already_activated: 'BACK_ERROR.INVITATION_ALREADY_ACTIVATED',
      invite_already_blocked: 'BACK_ERROR.INVITATION_ALREADY_BLOCKED',
      entity_is_not_found: 'BACK_ERROR.INVITATION_DEFAULT',
    };

    switch (err.error?.tag) {
      case enumRespErrors.InvitationOverdue:
      case enumRespErrors.InvitationAlreadyAccepted:
      case enumRespErrors.InviteAlreadyBlocked:
        this.alertService.modalError(errors[err.error?.tag]);
        break;
      case enumRespErrors.EntityIsNotFound:
        this.alertService.error(errors.entity_is_not_found);
        break;
      case enumRespErrors.InviteAlreadyActivated:
        break;
      default:
        this.alertService.error(errors.entity_is_not_found);
    }

    return of();
  }

  public update(id: number, organization: OrganizationModel): Observable<OrganizationModel> {
    return this.apiService.put<OrganizationModel>(`${environment.url.organizations}${id}`, organization);
  }

  public deleteDefaultPaymentMethod(id: number, isShowLoader: boolean): Observable<OrganizationModel> {
    return this.apiService.delete<OrganizationModel>(`${environment.url.organizations}${id}/default-payment-method`, null, {
      isShowLoader,
    });
  }

  public updateSubscription(organizationId: number, subscriptionId: number, isShowLoader?: boolean): Observable<OrganizationModel> {
    return this.apiService.put<OrganizationModel>(
      `${environment.url.organizations}${organizationId}/subscription/${subscriptionId}`,
      null,
      null,
      { isShowLoader },
    );
  }

  public deleteSubscription(organizationId: number, isShowLoader?: boolean): Observable<OrganizationModel> {
    return this.apiService.delete<OrganizationModel>(`${environment.url.organizations}${organizationId}/subscription`, null, {
      isShowLoader,
    });
  }

  public updatePaymentType(id: number, customsPaymentType: number, isShowLoader?: boolean): Observable<OrganizationModel> {
    return this.apiService.put<OrganizationModel>(
      `${environment.url.organizations}${id}/payment-type`,
      {
        customsPaymentType,
      },
      null,
      { isShowLoader },
    );
  }

  public updateBusinessDetails(organizationId: number, data: OrganizationBusinessDetailsModel, token?: string): Observable<void> {
    const options: any = !!token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : null;
    return this.apiService.put<void>(`${environment.url.organizations}${organizationId}/business-details`, data, options);
  }

  public getBrokerPoaFile(organizationId: number, isShowLoader: boolean = false): Observable<{ poaTemplateUrl: string }> {
    return this.apiService.get<{ poaTemplateUrl: string }>(`${environment.url.organizations}${organizationId}/broker-poa-file`, null, {
      isShowLoader,
    });
  }

  public createOrganizationCompany(organizationId: number, company: CompanyModel, token?: string): Observable<CompanyModel> {
    const options: any = !!token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : null;
    return this.apiService.post<CompanyModel>(`${environment.url.organizations}${organizationId}/companies`, company, options);
  }
}
